/**
 * Admin users list hook using React Relay.
 *
 * Server-side search (first/last/email ilike) and cursor pagination over
 * listAdminUsers. System membership is filtered through the nested
 * `adminSystems` relational filter, mirroring how the users list filters
 * listUsers via `userSystems`.
 *
 * NOTE: `AdminUserFilterInput.adminSystems` is a pending backend addition.
 * The filter is passed as a runtime variable so this compiles today, but the
 * API rejects it until the backend ships the field.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import CustomToast from '../../../../components/Redesing/custom-toast';
import type {
  useAdminUsersRelayDirectQuery as UseAdminUsersRelayDirectQuery,
  useAdminUsersRelayDirectQuery$variables,
  AdminUserFilterInput,
  AdminUserSortInput,
} from './__generated__/useAdminUsersRelayDirectQuery.graphql';

const AdminUsersListDirectQuery = graphql`
  query useAdminUsersRelayDirectQuery(
    $first: Int
    $after: String
    $filter: AdminUserFilterInput
    $sort: [AdminUserSortInput!]
  ) {
    listAdminUsers(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`;

export type { AdminUserFilterInput, AdminUserSortInput };

export interface AdminUserGraphQL {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Simplified filter options for common use cases
 */
export interface AdminUserQueryFilter {
  systemId?: string;
  search?: string;
}

function buildRelayFilter(
  filter?: AdminUserQueryFilter
): AdminUserFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: Record<string, unknown> = {};

  if (filter.systemId) {
    // Pending backend addition: AdminUserFilterInput.adminSystems
    relayFilter.adminSystems = { systemId: { eq: filter.systemId } };
  }
  if (filter.search) {
    const pattern = `%${filter.search}%`;
    relayFilter.or = [
      { firstName: { ilike: pattern } },
      { lastName: { ilike: pattern } },
      { email: { ilike: pattern } },
    ];
  }

  return Object.keys(relayFilter).length > 0
    ? (relayFilter as AdminUserFilterInput)
    : undefined;
}

/**
 * Fetches every page of admin users matching the filter. Used for CSV export,
 * where the table must receive the full filtered set rather than the pages
 * loaded so far.
 */
export async function fetchAllAdminUsers(
  filter?: AdminUserQueryFilter,
  sort?: AdminUserSortInput[]
): Promise<AdminUserGraphQL[]> {
  const allAdmins: AdminUserGraphQL[] = [];
  let after: string | undefined = undefined;
  let hasNextPage = true;

  while (hasNextPage) {
    const variables: useAdminUsersRelayDirectQuery$variables = {
      first: 250,
      after,
      filter: buildRelayFilter(filter),
      sort: sort ?? [{ field: 'FIRST_NAME', order: 'ASC' }],
    };

    const result = await fetchQuery<UseAdminUsersRelayDirectQuery>(
      relayEnvironment,
      AdminUsersListDirectQuery,
      variables
    ).toPromise();

    const connection = result?.listAdminUsers;
    const nodes = connection?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
    allAdmins.push(...(nodes as AdminUserGraphQL[]));

    hasNextPage = Boolean(
      connection?.pageInfo?.hasNextPage && connection?.pageInfo?.endCursor
    );
    after = connection?.pageInfo?.endCursor ?? undefined;
  }

  return allAdmins;
}

/**
 * Hook that provides React Query-like interface for the admin users list.
 *
 * Does NOT use Suspense - provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, count, hasMore, endCursor, isLoading } = useAdminUsersRelayQuery(
 *   { systemId: systemGid, search: 'jane' },
 *   { limit: 25, after: afterCursor }
 * );
 */
export function useAdminUsersRelayQuery(
  filter?: AdminUserQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: AdminUserSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<AdminUserGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'FIRST_NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useAdminUsersRelayDirectQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseAdminUsersRelayDirectQuery>(
          relayEnvironment,
          AdminUsersListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listAdminUsers?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as AdminUserGraphQL[]);
          setCount(result.listAdminUsers?.count ?? undefined);
          setHasMore(result.listAdminUsers?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listAdminUsers?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch admin users'));
        CustomToast({
          type: 'error',
          message: 'There was an error trying to fetch admin users.',
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
